'use client';
import React from 'react';
import LazyLoad from 'react-lazyload';
import { useConnatixLivestream } from '@benzinga/livestream-hooks';
import Hooks from '@benzinga/hooks';
import { Block } from '../Block';

const ConnatixVideoPlayer = React.lazy(() =>
  import('@benzinga/ads').then(module => {
    return { default: module.ConnatixVideoPlayer };
  }),
);

const ConnatixInitialHeadScript = React.lazy(() =>
  import('@benzinga/ads').then(module => {
    return { default: module.ConnatixInitialHeadScript };
  }),
);

interface ConnatixVideoProps {
  attrs?: {
    data?: {
      non_live_widget: unknown;
    };
  };
  fallback?: React.ReactNode;
}

export const ConnatixLiveBlock: React.FC<ConnatixVideoProps> = ({ attrs, fallback }) => {
  const { assembleLivestream, livestreamData } = useConnatixLivestream();
  const [isLive, setIsLive] = React.useState<boolean | null>(livestreamData ? true : null);

  Hooks.useEffectDidMount(() => {
    if (isLive !== null) {
      return;
    }

    let cancelled = false;
    assembleLivestream()
      .then(response => {
        if (!cancelled) {
          setIsLive(Boolean(response.enabled && response.data));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setIsLive(false);
        }
      });

    return () => {
      cancelled = true;
    };
  });

  if (isLive === null) {
    return fallback ?? <div aria-hidden className="min-h-[170px] w-full" />;
  }

  if (isLive && livestreamData) {
    return (
      <div className="block my-4">
        <React.Suspense fallback={<div aria-hidden className="min-h-[170px] w-full" />}>
          <ConnatixInitialHeadScript id={livestreamData.id} />

          <LazyLoad offset={100} once style={{ minHeight: 170, width: '100%' }}>
            <ConnatixVideoPlayer {...livestreamData} />
          </LazyLoad>
        </React.Suspense>
      </div>
    );
  }

  if (!isLive && attrs?.data?.non_live_widget) {
    return <Block block={attrs?.data?.non_live_widget} />;
  }

  if (isLive) {
    return fallback ?? <div aria-hidden className="min-h-[170px] w-full" />;
  }

  return fallback ?? null;
};
